import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography,
    CircularProgress, 
    TextField, 
    Button,
    FormControlLabel, 
    Checkbox,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const CrewScheduleCodeCategoryPage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const { crewScheduleCodeCategoryId } = useParams();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formValues, setFormValues] = useState({
        categoryCode: '',
        description: '',
        sortOrder: 0,
        isVisible: true,
    });

    useEffect(() => {
        const fetchCategory = async () => {
            if (!crewScheduleCodeCategoryId) return;
            try {
                setLoading(true);
                const res = await api.get(
                    `/crewschedulecodecategories/${crewScheduleCodeCategoryId}`
                );
                
                const formData = res?.data || {};
                setFormValues({
                    categoryCode: formData.categoryCode || '',
                    description: formData.description || '',
                    sortOrder: formData.sortOrder ?? 0,
                    isVisible: formData.isVisible ?? true,
                });
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategory();
    }, [crewScheduleCodeCategoryId]);

    const checkoutSchema = yup.object().shape({
        categoryCode: yup.string().required('Required'),
        sortOrder:    yup.number().integer().min(0),
    });

    const handleFormSubmit = async (values) => {
        try {
            const method = crewScheduleCodeCategoryId ? 'PUT' : 'POST';
            const url = crewScheduleCodeCategoryId
                ? `/crewschedulecodecategories/update/${crewScheduleCodeCategoryId}`
                : '/crewschedulecodecategories/new';

            const response = await api({ method, url, data: values });

            if (![200, 201].includes(response.status)) {
                toast.error('Failed to submit the form.');
                return;
            }

            toast.success(crewScheduleCodeCategoryId
                ? 'Category updated successfully.'
                : 'Category created successfully.'
            );
            navigate("/crewschedulecodecategories");
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || 'Failed to submit the form.');
            setError(error);
        }
    };

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Schedule Code Category"
                    subtitle={loading ? 'Loading...' : crewScheduleCodeCategoryId ? 'Update' : 'Create'}
                />
                {!loading && (
                    <StyledButton onClick={() => navigate("/crewschedulecodecategories")}>
                        <ArrowBackIosNewIcon />
                    </StyledButton>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <Typography color="error">
                    Error: {error?.message || ''}
                </Typography>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    gap={2}
                                >
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        required
                                        label="Category Code"
                                        name="categoryCode"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.categoryCode}
                                        error={!!touched.categoryCode && !!errors.categoryCode}
                                        helperText={touched.categoryCode && errors.categoryCode}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Sort Order"
                                        name="sortOrder"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.sortOrder}
                                        error={!!touched.sortOrder && !!errors.sortOrder}
                                        helperText={touched.sortOrder && errors.sortOrder}
                                        slotProps={{
                                            input: { sx: { "& input": { textAlign: "right" } } },
                                        }}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        label="Description"
                                        name="description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 12' }}
                                        control={
                                            <Checkbox
                                                name="isVisible"
                                                checked={values.isVisible}
                                                onChange={handleChange}
                                                color="primary"
                                            />
                                        }
                                        label="Is Visible"
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {crewScheduleCodeCategoryId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default CrewScheduleCodeCategoryPage;
